import React, { useState } from 'react';
import PropTypes from 'prop-types';

import LanguageConverter from '@careerstart/wae-common/src/main/helperFunction/LanguageConverter';
import { Grid, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import ClockIcon from '../../../../assets/icons/ClockIcon';
import CommonTooltip from '../../../../components/Tooltip/Tooltip';
import { BLACK, FEED_BACK, RED } from '../../../../theme/colorConstants';
import { PRIMARY_FONT } from '../../../../theme/fontConstants';
import { epochToCountdown } from '../../../../utils';
import {
  pendingJobKeys,
  pendingJobStatuses,
  statusActionAText,
  statusActionBText,
} from '../../constants';

import PendingStatusMenuButton from './PendingStatusMenuButton';
import RosterCardContainer from './RosterCardContainer';
import RosterCardMoreButton from './RosterCardMoreButton';
import rosterPropTextStyles from './rosterCardSharedStyles';
import WorkerReportButton from './WorkerReportButton';

const ReservationTooltipContent = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      alignItems: 'center',
      width: '220px',
    }}
  >
    <Typography
      sx={{
        fontFamily: PRIMARY_FONT[700],
        fontSize: '16px',
        marginBottom: '4px',
        color: BLACK[100],
      }}
    >
      {LanguageConverter('jobOrder.roster.reservationTooltipTitle')}
    </Typography>
    <Typography
      sx={{
        fontFamily: PRIMARY_FONT[400],
        fontSize: '14px',
        color: BLACK[60],
        textAlign: 'center',
      }}
    >
      {LanguageConverter('jobOrder.roster.reservationTooltip')}
    </Typography>
  </Box>
);

const reservationValueTextStyles = {
  fontFamily: PRIMARY_FONT[700],
  fontSize: '14px',
  color: BLACK[80],
};

const ReservationDisplay = ({ seatHoldingExpiresAt }) => {
  const countdown = epochToCountdown(seatHoldingExpiresAt);

  if (countdown === null) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <ClockIcon />
        <Typography sx={reservationValueTextStyles}>{LanguageConverter('noResults.na')}</Typography>
      </Box>
    );
  }

  if (countdown.expired) {
    return (
      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: RED[80] }}>
        {LanguageConverter('jobOrder.roster.reservationExpired')}
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <ClockIcon />
      <Typography sx={reservationValueTextStyles}>{countdown.display}</Typography>
    </Box>
  );
};

ReservationDisplay.propTypes = {
  seatHoldingExpiresAt: PropTypes.number,
};

const PendingEmployeeRosterCard = ({
  placement,
  onRevokeOrAcceptInvite,
  onResendOrRejectInvite,
  status,
  onManageCandidate,
  onFavoriteClick,
}) => {
  const [isWorkerReportOpen, setIsWorkerReportOpen] = useState(false);
  const statusMessage =
    (pendingJobStatuses[status] && LanguageConverter(pendingJobStatuses[status])) || '';

  const pendingMenuActions = [
    {
      text: (statusActionAText[status] && LanguageConverter(statusActionAText[status])) || '',
      onClick: onRevokeOrAcceptInvite,
    },
    {
      text: (statusActionBText[status] && LanguageConverter(statusActionBText[status])) || '',
      onClick: onResendOrRejectInvite,
    },
  ];

  const showReservation = status === pendingJobKeys.INV || status === pendingJobKeys.OFFER;
  const isApplication = status === pendingJobKeys.APPLICATION;

  return (
    <RosterCardContainer
      dataTestId="pendingRosterCard"
      isFavorite={placement?.isFavorite}
      reliabilityScore={placement?.reliabilityScore}
      rawReliability={placement?.rawReliability}
      rawAls={placement?.rawAls}
      isPriorityResourcePool={placement?.isPriorityResourcePool}
      firstTimeWorkingThisCustomer={placement?.firstTimeWorkingThisCustomer}
      gpsData={placement?.gpsData}
      gpsPings={placement?.gpsPings}
      employeeName={placement?.employeeName}
      employeeId={placement?.employeeId}
      agencyId={placement?.agencyId}
      profilePictureSrc={placement?.employeeProfilePicture}
      avatarAlt={placement?.employeeName}
      isWorkerReportOpen={isWorkerReportOpen}
      onAcceptApplication={isApplication ? onRevokeOrAcceptInvite : undefined}
      onDeclineApplication={isApplication ? onResendOrRejectInvite : undefined}
      onFavoriteClick={onFavoriteClick}
      employeePhoneNumber={placement?.employeePhoneNumber}
    >
      <Grid item>
        <Grid container alignItems="center" gap="12px">
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography sx={rosterPropTextStyles}>
              {LanguageConverter('jobOrder.roster.status')}
            </Typography>
            <PendingStatusMenuButton
              status={statusMessage}
              actions={pendingMenuActions}
              color={status === pendingJobKeys.APPLICATION ? FEED_BACK.GREEN_80 : undefined}
            />
          </Box>

          {showReservation && (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography sx={rosterPropTextStyles}>
                {LanguageConverter('jobOrder.roster.reservation')}
              </Typography>
              <CommonTooltip
                title={<ReservationTooltipContent />}
                placement="top"
                bgcolor={BLACK[0]}
                color={BLACK[80]}
                enterDelay={200}
              >
                <ReservationDisplay seatHoldingExpiresAt={placement?.seatHoldingExpiresAt} />
              </CommonTooltip>
            </Box>
          )}
          <WorkerReportButton
            isOpen={isWorkerReportOpen}
            onClick={() => setIsWorkerReportOpen((prev) => !prev)}
          />
          <RosterCardMoreButton onClick={onManageCandidate} />
        </Grid>
      </Grid>
    </RosterCardContainer>
  );
};

PendingEmployeeRosterCard.propTypes = {
  placement: PropTypes.shape({
    id: PropTypes.string,
    employeeId: PropTypes.string,
    employeeName: PropTypes.string,
    employeePhoneNumber: PropTypes.string,
    agencyId: PropTypes.string,
    employeeProfilePicture: PropTypes.string,
    isFavorite: PropTypes.bool,
    isOnAutoPlacementList: PropTypes.bool,
    reliabilityScore: PropTypes.number,
    rawReliability: PropTypes.number,
    rawAls: PropTypes.number,
    isPriorityResourcePool: PropTypes.bool,
    firstTimeWorkingThisCustomer: PropTypes.bool,
    gpsData: PropTypes.shape({ onsite: PropTypes.bool }),
    gpsPings: PropTypes.arrayOf(PropTypes.shape({ onsite: PropTypes.bool })),
    status: PropTypes.string,
    seatHoldingExpiresAt: PropTypes.number,
  }),
  onRevokeOrAcceptInvite: PropTypes.func,
  onResendOrRejectInvite: PropTypes.func,
  status: PropTypes.string,
  onManageCandidate: PropTypes.func,
  onFavoriteClick: PropTypes.func,
};

export default PendingEmployeeRosterCard;
